/**
 * Серверное состояние набора тем (#845): список и правки. Через `useAuth`, а
 * не `useRole`: экран живёт в разделе «Голос клиента», который открывается и
 * в портале, и во встроенном Bitrix24.
 *
 * Правки не показывают тост: код отказа (кап, дубль, «тему изменили») решает,
 * что сказать, и говорит это форма рядом с кнопкой.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { CONVERSATION_THEMES_FLAG_KEY } from '@aica/core';
import { canEditThemeSet } from '@aica/core/rbac';
import type {
  CreateOrgThemeRequest,
  UpdateOrgThemeRequest,
} from '@aica/api-types/schemas';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { useFeatureFlagsSafe } from '@/contexts/FeatureFlagsContext';
import {
  useLockedDemoAsked,
  useLockedDemoDeclined,
  useLockedDemoFlag,
} from '@/contexts/LockedDemoContext';
import { useOptionalPermissions } from '@/contexts/PermissionContext';
import { useScreenSubject } from '@/contexts/ScreenSubjectContext';
import { managedAccessOf } from '@/lib/route-permissions';
import { retryTransientOnly } from '@/lib/query-retry';
import { objectionStreamThemeFilter } from '../../objections/useObjectionStream';
import {
  voiceThemeAnalyticsFilter,
  voiceThemeFilteredFilter,
} from '../useVoiceOfCustomer';

export const voiceThemeKeys = {
  list: (orgId: string | null) => ['analytics', 'voice-themes', orgId] as const,
};

/**
 * Whether the theme set is open for this organization (`enableConversationThemes`,
 * a beta on Pro and up — #845).
 * The server's flag map once it has loaded: `/feature-flags` on the web, the
 * session's `flags` subset in embedded Bitrix24 (#1347). A failed or pending
 * load reads as off — no button. The routes refuse without the flag on their own.
 *
 * On the demo of a closed section (#2816) the themes are read from the demo
 * too: a plan below the themes' own (`tier_not_eligible`) does not hide them —
 * the routes answer with the demo. The client gate cannot see an administrator's
 * switch there (below the plan the tier verdict wins over `feature_disabled`),
 * so the set's own list is asked and its refusal says it: a 403 `themes_disabled`
 * hides the themes — no button, no picker, no filter, and nothing said. So
 * does every other refusal (403) of the list there — `tier_not_eligible` when
 * the demo window closed with the page open (the first real record arrived):
 * the routes no longer answer the demo, and a button would open an error
 * panel. The list is the one the panel reads (same key); while it is still on
 * its way — or failed for a reason that is not a refusal — the themes are
 * offered, as the demo answers them.
 */
export function useVoiceThemesAvailable(): boolean {
  const { isEnabled, isReady, gate } = useFeatureFlagsSafe();
  const lockedDemo = useLockedDemoFlag();
  const demoThemes =
    lockedDemo !== null && gate(CONVERSATION_THEMES_FLAG_KEY) === 'tier_not_eligible';
  // The probe is the list itself, an observer of the panel's key: it does not
  // run outside the demo of a closed section, and is not asked again on every
  // mount — a refetch of a refused list drops the refusal while it runs, and
  // the button would come back for the length of one request.
  const list = useVoiceThemes({ enabled: demoThemes, retryOnMount: false });
  // No organization yet (auth still resolving): the set's query could not run
  // and the panel would wait forever — no button until there is one.
  const { orgId } = useAuth();
  if (!orgId || !isReady) return false;
  if (demoThemes) return (list.error as { status?: unknown } | null)?.status !== 403;
  return isEnabled(CONVERSATION_THEMES_FLAG_KEY);
}

export function useVoiceThemes(
  options: { enabled?: boolean; retryOnMount?: boolean } = {}
) {
  const { apiClient, orgId } = useAuth();
  // The demo set of a closed section and the customer's own set are different
  // answers (#2816): a plan change with the app open must not serve one for the
  // other from the cache. Invalidation goes by the `list(orgId)` prefix.
  const lockedDemo = useLockedDemoAsked();
  // The screen declines the demo (`includeDemo=no`): the route refuses below
  // the plan rather than answering the demo set, and that is another answer.
  const declined = useLockedDemoDeclined();
  return useQuery({
    queryKey: [
      ...voiceThemeKeys.list(orgId),
      lockedDemo ? 'demo' : 'own',
      ...(declined ? ['no-demo'] : []),
    ],
    queryFn: () => apiClient.voiceThemes.list(declined ? { includeDemo: 'no' } : {}),
    enabled: Boolean(orgId) && (options.enabled ?? true),
    // The client's verdict can lag the server's (a plan change with the app
    // open): an answer of the other mode than the key's — the demo set under
    // `own`, the own set under `demo` — is not kept, so a verdict that catches
    // up never reads it back as fresh and offers edits on demo themes.
    staleTime: (query) =>
      (query.state.data?.demoOnly === true) === lockedDemo ? 30 * 1000 : 0,
    retry: retryTransientOnly,
    retryOnMount: options.retryOnMount ?? true,
  });
}

/**
 * Кто правит набор — `organization.update` (client, admin; Р5). Только для
 * кнопок: решает сервер, и роль без права видит набор без правок.
 *
 * - Портал: роль С УЧЁТОМ «смотреть как» (`effectiveRole`) — превью показывает
 *   то, что увидит эта роль, и флаг владельца платформы его не перекрывает
 *   (как у критериев, #1253).
 * - Bitrix24: роль сессии (`useAuth`), владельца платформы там не бывает.
 * - Грант уже, чем организация (`/access/me`): правку набора закрывает шлюз
 *   возможностей (роль ∧ грант, `require-capability.ts`) — кнопки не
 *   показываются, а не ведут к 403.
 */
export function useCanEditVoiceThemes(): boolean {
  const { role } = useAuth();
  // The demo of a closed section is read-only: the set is not the customer's yet.
  const lockedDemo = useLockedDemoFlag();
  const permissions = useOptionalPermissions();
  const subject = useScreenSubject();
  // `access` absent — a context that never asked `/access/me`: the role decides.
  const grant = subject?.access ? managedAccessOf(subject.access) : null;
  if (lockedDemo) return false;
  if (grant && grant.scope.kind !== 'org') return false;
  if (permissions) {
    const previewing = permissions.effectiveRole !== permissions.globalRole;
    return canEditThemeSet(permissions.effectiveRole, permissions.isOwner && !previewing);
  }
  return canEditThemeSet(role);
}

/**
 * Кто видит предложения новых тем: тот, кто правит набор, И чей грант (если он
 * есть) называет `records.read` — маршрут предложений требует `aggregates.voc`
 * и `records.read` (примеры разговоров), и без второго отвечает 403. Запрос,
 * заведомо обречённый на отказ, не уходит.
 */
export function useCanReadThemeProposals(): boolean {
  const canEdit = useCanEditVoiceThemes();
  const subject = useScreenSubject();
  const grant = subject?.access ? managedAccessOf(subject.access) : null;
  return canEdit && (!grant || grant.capabilities.includes('records.read'));
}

/**
 * Список набора перечитывается после любой правки, удалась она или нет (строки
 * и счёт капа): отказ — «тему изменили», дубль, кап — значит, экран был
 * устаревшим. Промис — именно это перечитывание: правка (`onSettled`) ждёт его,
 * и кнопка «Сохранить» не висит, пока блок тем идёт за тяжёлым запросом в
 * Postgres.
 */
export function refreshVoiceThemeList(
  queryClient: QueryClient,
  orgId: string | null
): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: voiceThemeKeys.list(orgId) });
}

/**
 * Набор изменился: блок тем во вкладках раздела (#845 PR 6) — его строки,
 * определения и база строки считаются по набору — помечается устаревшим и
 * перечитывается без ожидания (ошибку перечитывания `invalidateQueries` не
 * пробрасывает), а ответы под фильтром темы (#2229) вне экрана сбрасываются,
 * на экране — перечитываются. Звать только на УСПЕХЕ правки, которая меняет набор: отказ и
 * отклонённое предложение набор не трогают, а аналитика — тяжёлый, некэшируемый
 * запрос в Postgres.
 */
export function invalidateVoiceThemeAnalytics(
  queryClient: QueryClient,
  orgId: string | null
): void {
  void queryClient.invalidateQueries(voiceThemeAnalyticsFilter(orgId));
  // Answers narrowed by a theme (#2229, spec §3): the ones off screen are
  // dropped — after an archive or a new definition their numbers are no
  // longer that theme's, and a stale entry would be drawn on the way back to
  // a tab. The ones on screen keep their numbers while they refetch (the tab,
  // its open rows and a FAQ draft built under it stay put) and hear a 400 if
  // the theme is gone.
  for (const filter of [
    voiceThemeFilteredFilter(orgId),
    objectionStreamThemeFilter(orgId),
  ]) {
    void queryClient.resetQueries({ ...filter, type: 'inactive' });
    void queryClient.invalidateQueries({ ...filter, type: 'active' });
  }
}

/** What every set-changing mutation does: the list always, the analytics on success. */
function useThemeSetInvalidation() {
  const queryClient = useQueryClient();
  const { orgId } = useAuth();
  return {
    onSuccess: () => invalidateVoiceThemeAnalytics(queryClient, orgId),
    onSettled: () => refreshVoiceThemeList(queryClient, orgId),
  };
}

export function useCreateVoiceTheme() {
  const { apiClient } = useAuth();
  const invalidation = useThemeSetInvalidation();
  return useMutation({
    mutationFn: (body: CreateOrgThemeRequest) => apiClient.voiceThemes.create(body),
    ...invalidation,
  });
}

export function useUpdateVoiceTheme() {
  const { apiClient } = useAuth();
  const invalidation = useThemeSetInvalidation();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateOrgThemeRequest }) =>
      apiClient.voiceThemes.update(id, body),
    ...invalidation,
  });
}

export function useSetVoiceThemeArchived() {
  const { apiClient } = useAuth();
  const invalidation = useThemeSetInvalidation();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      archived ? apiClient.voiceThemes.archive(id) : apiClient.voiceThemes.unarchive(id),
    ...invalidation,
  });
}
